import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataProtection } from '@aptsi-types';
import { environment } from '@env';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlingService } from 'src/app/shared/errorHandling/errorHandling.service';
import { API_ENDPOINTS } from '../../data-protection-api-endpoints';

export interface ImplementationDetails {
  programId?: string;
  implementationStatus?: string;
  protectionType?: string;
  algorithmOrMethod?: string;
  applicableStandards?: string[];
  implementationOwner?: string;
  implementationOwnerKeycloakId?: string;
  plannedStartDate?: string;
  plannedCompletionDate?: string;
  actualImplementationDate?: string;
  implementationNotes?: string;
  implementationEvidenceUri?: string;
  createdBy?: string;
  createdByKeycloakId?: string;
}

@Injectable({ providedIn: 'root' })
export class DataAssetMappingService {
  private readonly dataProtectionProgramsUrl = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlingService: ErrorHandlingService,
  ) {}

  FetchMappedDataAssetsWithProgramStatus(currentPage: number): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_MAPPED_DATA_ASSETS_WITH_PROGRAM_STATUS}`,
      { currentPage },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  FetchMappedDataAssetsWithProgramStatusSearch(
    currentPage: number,
    searchText: string,
    filterByHeader: string,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_MAPPED_DATA_ASSETS_WITH_PROGRAM_STATUS_SEARCH}`,
      { currentPage, searchText, filterByHeader },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // Step 1: Create program details. Audit values come from the current Keycloak session.
  CreateProgramDetails(
    mappingId: string,
    name: string,
    description: string,
    programStartDate: string,
    targetCompletionDate: string,
    programOwner: string,
    programOwnerKeycloakId: string,
    createdBy: string,
    createdByKeycloakId: string,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.CREATE_PROGRAM_DETAILS}`,
      {
        mappingId,
        name,
        description,
        programStartDate,
        targetCompletionDate,
        programOwner,
        programOwnerKeycloakId,
        createdBy,
        createdByKeycloakId,
      },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // IDs are UUID strings in the API, not numeric IDs.
  FetchProgramDetailsByMappingId(mappingId: string): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_PROGRAM_DETAILS_BY_MAPPING_ID}`,
      { mappingId },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  UpdateProgramDetails(
    programId: string,
    name: string,
    description: string,
    programStartDate: string,
    targetCompletionDate: string,
    programOwner: string,
    programOwnerKeycloakId: string,
    programStatus: string,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.UPDATE_PROGRAM_DETAILS}`,
      {
        programId,
        name,
        description,
        programStartDate,
        targetCompletionDate,
        programOwner,
        programOwnerKeycloakId,
        programStatus,
      },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // Step 2: Create implementation details for an implementation already completed.
  // This method is called without plannedStartDate/plannedCompletionDate.
  CreateImplementationDetailsForImplementedStatus(
    implementationReqBody: ImplementationDetails,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.CREATE_IMPLEMENTATION_DETAILS}`,
      { implementationReqBody },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // Step 2: Create implementation details still in progress.
  // This method is called without actualImplementationDate.
  CreateImplementationDetailsForNotImplementedStatus(
    implementationReqBody: ImplementationDetails,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.CREATE_IMPLEMENTATION_DETAILS}`,
      { implementationReqBody },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  FetchImplementationDetailsByProgramId(programId: string): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_IMPLEMENTATION_DETAILS_BY_PROGRAM_ID}`,
      { programId },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  UpdateImplementationDetailsForImplementedStatus(
    implementationReqBody: ImplementationDetails,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.UPDATE_IMPLEMENTATION_DETAILS}`,
      { implementationReqBody },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  UpdateImplementationDetailsForNotImplementedStatus(
    implementationReqBody: ImplementationDetails,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.UPDATE_IMPLEMENTATION_DETAILS}`,
      { implementationReqBody },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // Step 3: Verification creation. verificationDate is included because it is part of the UI request.
  CreateVerificationDetails(
    programId: string,
    verificationStatus: string,
    verifiedBy: string,
    verifiedByKeycloakId: string,
    verificationDate: string,
    verificationResult: string,
    verificationEvidenceUri: string,
    createdBy: string,
    createdByKeycloakId: string,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.CREATE_VERIFICATION_DETAILS}`,
      {
        programId,
        verificationStatus,
        verifiedBy,
        verifiedByKeycloakId,
        verificationDate,
        verificationResult,
        verificationEvidenceUri,
        createdBy,
        createdByKeycloakId,
      },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  FetchVerificationDetailsByProgramId(programId: string): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_VERIFICATION_DETAILS_BY_PROGRAM_ID}`,
      { programId },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

  // The service method name preserves the existing API/service spelling.
  UpdateerificationDetails(
    programId: string,
    verificationResult: string,
    verificationEvidenceUri: string,
  ): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.UPDATE_VERIFICATION_DETAILS}`,
      { programId, verificationResult, verificationEvidenceUri },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }


  FetchCompletedProgramDetailsByMappingId(mappingId: string): Observable<DataProtection[]> {
    return this.http.post<DataProtection[]>(
      `${this.dataProtectionProgramsUrl}${API_ENDPOINTS.DATAPROTECTIONPROGRAM.FETCH_COMPLETED_PROGRAM_DETAILS_BY_MAPPING_ID}`,
      { mappingId },
    ).pipe(
      map(response => response),
      catchError(error => this.errorHandlingService.handleError(error)),
    );
  }

}
